// src/components/orders/MyOrdersTab.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { orderService } from '../../services/orderService';
import { paymentService } from '../../services/paymentService';

const STATUS_MAP = {
  pending: { bg: 'rgba(255,152,0,0.12)', color: '#ff9800', border: '#ff9800', label: 'Pending Payment' },
  paid:    { bg: 'rgba(0,200,81,0.12)',  color: '#00C851', border: '#00C851', label: 'Paid' },
  failed:  { bg: 'rgba(195,0,0,0.1)',   color: '#c30000', border: '#c30000', label: 'Failed' },
};

const MyOrdersTab = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [payingOrderId, setPayingOrderId] = useState(null);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const data = await orderService.getAllOrders();
      setOrders(Array.isArray(data) ? data : []);
    } catch {
      setError('Failed to load your orders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const loadRazorpay = () =>
    new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const s = document.createElement('script');
      s.src = 'https://checkout.razorpay.com/v1/checkout.js';
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });

  const handlePayRemaining = async (order) => {
    try {
      setPayingOrderId(order.id);
      const loaded = await loadRazorpay();
      if (!loaded) { alert('Payment gateway failed to load. Please try again.'); return; }

      const paymentIntent = await paymentService.initiateOrderPayment(order.id);

      const options = {
        key:         paymentIntent.razorpay_key,
        amount:      paymentIntent.amount_paisa,
        currency:    'INR',
        name:        'AucwiniCars',
        description: `Final Payment — ${order.car?.brand || ''} ${order.car?.model || ''}`,
        order_id:    paymentIntent.razorpay_order_id,
        handler: async (response) => {
          try {
            const confirmResponse = await paymentService.confirmPayment(
              paymentIntent.payment_id,
              response
            );
            navigate('/payment-success', {
              state: { paymentData: confirmResponse, paymentIntent, order, car: order.car, type: 'order' }
            });
          } catch {
            alert('Payment confirmation failed. Please contact support.');
            setPayingOrderId(null);
          }
        },
        prefill: {},
        theme: { color: '#003566' },
        modal: { ondismiss: () => setPayingOrderId(null) }
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', () => {
        alert('Payment failed. Please try again.');
        setPayingOrderId(null);
      });
      rzp.open();
    } catch (err) {
      alert(err?.error || 'Failed to initiate payment. Please try again.');
      setPayingOrderId(null);
    }
  };

  const fmt = (d) => new Date(d).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });

  const getImgSrc = (urls) => {
    if (!urls?.length) return null;
    const u = urls[0];
    return u.startsWith('http') ? u : `http://localhost:3001${u}`;
  };

  if (loading) return (
    <div className="tab-content" style={{ display: 'block', width: '100%' }}>
      <div className="state-center"><div className="spinner-large"></div><p>Loading your orders...</p></div>
    </div>
  );

  if (error) return (
    <div className="tab-content" style={{ display: 'block', width: '100%' }}>
      <div className="state-center">
        <span style={{ fontSize: 48 }}>⚠️</span>
        <h3 style={{ color: '#c30000' }}>{error}</h3>
        <button className="btn-primary" onClick={fetchOrders}>Retry</button>
      </div>
    </div>
  );

  return (
    <div className="tab-content" style={{ display: 'block', width: '100%', boxSizing: 'border-box' }}>
      <div className="tab-header">
        <h2 className="tab-title">My Orders</h2>
        <p className="tab-subtitle">
          {orders.length === 0 ? 'No orders yet' : `${orders.length} order${orders.length !== 1 ? 's' : ''} found`}
        </p>
      </div>

      {orders.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🛒</div>
          <h3>No Orders Yet</h3>
          <p>Book a car to start your purchase journey</p>
          <button className="btn-primary" onClick={() => window.location.href = '/buy-cars'}>Browse Cars</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, width: '100%' }}>
          {orders.map((order) => {
            const st       = STATUS_MAP[order.status] || STATUS_MAP.pending;
            const imgSrc   = getImgSrc(order.car?.images_urls);
            // Use dedicated fields from backend; fall back gracefully
            const carPrice    = Number(order.car_price   ?? order.car?.price ?? 0);
            const bookingPaid = Number(order.booking_paid ?? order.booking?.amount_rupees ?? 0);
            const remaining   = Number(order.amount       ?? 0);  // order.amount IS the remaining amount
            const isPending   = order.status === 'pending';
            const isPaying    = payingOrderId === order.id;

            return (
              <div key={order.id} style={cardStyle}>

                {/* ── Header ── */}
                <div style={headerStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 0 }}>
                    {imgSrc
                      ? <img src={imgSrc} alt={order.car?.title}
                          style={{ width: 80, height: 60, borderRadius: 10, objectFit: 'cover', border: '1px solid #eee', flexShrink: 0 }}
                          onError={e => { e.target.style.display = 'none'; }} />
                      : <div style={{ width: 80, height: 60, borderRadius: 10, background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, flexShrink: 0 }}>🚗</div>
                    }
                    <div style={{ minWidth: 0 }}>
                      <h3 style={{ margin: '0 0 3px', fontSize: 17, fontWeight: 800, color: '#003566', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {order.car?.title || `${order.car?.brand} ${order.car?.model}`}
                      </h3>
                      <p style={{ margin: 0, fontSize: 13, color: '#999' }}>
                        {order.car?.brand} {order.car?.model}{order.car?.year ? ` • ${order.car.year}` : ''}
                      </p>
                    </div>
                  </div>
                  <span style={{ padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                    textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap', flexShrink: 0,
                    background: st.bg, color: st.color, border: `1.5px solid ${st.border}` }}>
                    {st.label}
                  </span>
                </div>

                {/* ── Details grid ── */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', padding: '0 24px', borderBottom: '1px solid #f5f5f5' }}>
                  {[
                    { label: 'Order ID',    value: `#${order.id}` },
                    { label: 'Order Date',  value: fmt(order.created_at) },
                    { label: 'Car Location', value: order.car?.location || '—' },
                  ].map(({ label, value }) => (
                    <div key={label} style={{ display: 'flex', flexDirection: 'column', padding: '14px 16px 14px 0' }}>
                      <span style={{ fontSize: 11, color: '#bbb', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>{label}</span>
                      <span style={{ fontSize: 14, color: '#222', fontWeight: 700 }}>{value}</span>
                    </div>
                  ))}
                </div>

                {/* ── Price strip ── */}
                <div style={{ display: 'flex', alignItems: 'center', background: '#f8f9fa', padding: '16px 24px', borderBottom: '1px solid #f0f0f0', flexWrap: 'wrap', gap: 12 }}>
                  <PriceItem label="Car Price"        value={`₹${carPrice.toLocaleString('en-IN')}`} />
                  <Divider />
                  <PriceItem label="Booking Paid"     value={`₹${bookingPaid.toLocaleString('en-IN')}`}  valueColor="#00C851" />
                  <Divider />
                  <PriceItem label="Remaining Amount" value={`₹${remaining.toLocaleString('en-IN')}`}    valueColor={isPending ? '#ff9800' : '#00C851'} />
                </div>

                {/* ── Actions ── */}
                <div style={{ display: 'flex', gap: 12, padding: '16px 24px' }}>
                  <button className="btn-view-car" onClick={() => window.location.href = `/car/${order.car?.id}`} style={{ flex: 1 }}>
                    View Car →
                  </button>

                  {isPending && (
                    <button
                      disabled={isPaying}
                      onClick={() => handlePayRemaining(order)}
                      style={{
                        flex: 2, padding: '11px 20px',
                        background: isPaying ? 'rgba(255,214,10,0.5)' : 'linear-gradient(135deg,#FFD60A,#FFC700)',
                        color: '#003566', border: 'none', borderRadius: 10,
                        fontSize: 14, fontWeight: 800,
                        cursor: isPaying ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        boxShadow: isPaying ? 'none' : '0 4px 14px rgba(255,214,10,0.35)',
                        transition: 'all 0.2s'
                      }}
                    >
                      {isPaying
                        ? <><span className="spinner-small"></span> Processing...</>
                        : <>💳 Pay Remaining ₹{remaining.toLocaleString('en-IN')}</>}
                    </button>
                  )}

                  {order.status === 'paid' && (
                    <div style={{ flex: 2, padding: '11px 20px', background: 'rgba(0,200,81,0.1)',
                      color: '#00C851', border: '1.5px solid #00C851', borderRadius: 10,
                      fontSize: 14, fontWeight: 700,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                      ✅ Payment Complete — Delivery Scheduled
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* CTA */}
          <div className="browse-cta-card">
            <div className="browse-cta-card-text">
              <h3>Looking for more cars?</h3>
              <p>Browse our entire collection.</p>
            </div>
            <button className="btn-primary" onClick={() => window.location.href = '/buy-cars'}>Browse Cars</button>
          </div>
        </div>
      )}
    </div>
  );
};

const PriceItem = ({ label, value, valueColor = '#333' }) => (
  <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 80 }}>
    <span style={{ fontSize: 11, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>{label}</span>
    <strong style={{ fontSize: 18, fontWeight: 800, color: valueColor }}>{value}</strong>
  </div>
);

const Divider = () => (
  <div style={{ width: 1, height: 40, background: '#e0e0e0', margin: '0 8px', flexShrink: 0 }} />
);

const cardStyle = {
  display: 'block', width: '100%', boxSizing: 'border-box',
  background: '#fff', border: '1.5px solid #eee', borderLeft: '4px solid #FFD60A',
  borderRadius: 18, overflow: 'hidden', transition: 'box-shadow 0.2s',
};

const headerStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '20px 24px', borderBottom: '1px solid #f5f5f5',
  gap: 16, flexWrap: 'wrap', width: '100%', boxSizing: 'border-box',
};

export default MyOrdersTab;